import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TEST_IDS } from "@/lib/testIds";
import { useAuth } from "@/context/AuthContext";
import WaitlistDialog from "@/components/WaitlistDialog";
import { Sparkles, LineChart, Repeat, Layers, ArrowRight, Bell, Link2, Newspaper, PlayCircle, TrendingUp, Check, Database, Calendar, Bot, Target, Wallet, Zap, Wand2, Smartphone, Compass } from "lucide-react";
import LandingDividendPreview from "@/components/LandingDividendPreview";
import LandingAIAdvisor from "@/components/LandingAIAdvisor";
import LandingBacktestCompare from "@/components/LandingBacktestCompare";
import usePageSeo from "@/hooks/usePageSeo";
import useSiteCopy from "@/hooks/useSiteCopy";
import api from "@/lib/api";
import PlanPickerQuiz from "@/components/pricing/PlanPickerQuiz";

// "Why investors choose DPP" — 7 concise proof points that mirror the
// reference layout. Icons pulled from lucide so the row scales cleanly
// down to mobile as a horizontal-scroll strip. Each tile also carries the
// slug of the /vs/field scorecard area it deep-links to so a tile click
// lands the visitor on the exact row on the scorecard.
const WHY_DPP = [
  { icon: Database,   title: "Real Market Data",        desc: "Prices & dividends for 8,000+ tickers",           tone: "sky",     areaAnchor: "area-research" },
  { icon: LineChart,  title: "Backtest Any Portfolio",  desc: "30+ years with DRIP & monthly contributions",     tone: "emerald", areaAnchor: "area-analysis" },
  { icon: Target,     title: "Income Goal Planner",     desc: "Know exactly what to invest for your goal",       tone: "rose",    areaAnchor: "area-ai" },
  { icon: Link2,      title: "Broker Sync",             desc: "Live value & forward income from real accounts",  tone: "teal",    areaAnchor: "area-holdings" },
  { icon: Wand2,      title: "Holdings Optimizer",      desc: "AI rebalancing with a whole-share trade list",    tone: "amber",   areaAnchor: "area-ai" },
  { icon: Bot,        title: "Ask DPP",                 desc: "An AI analyst that runs the numbers for you",     tone: "violet",  areaAnchor: "area-ai" },
  { icon: Bell,       title: "Ex-Dividend Alerts",      desc: "Morning phone alerts before every ex-date",       tone: "orange",  areaAnchor: "area-dividends" },
  { icon: Smartphone, title: "iPhone & Android Apps",   desc: "Your dividend plan in your pocket",               tone: "blue",    areaAnchor: "area-holdings" },
];

// Named-tone → Tailwind color pair mapping. Kept in one place so a
// single-word `tone` prop drives both bg tint and icon color for every
// icon-in-a-circle used on the landing page (stats strip + Why-DPP).
const TONE_STYLES = {
  sky:     "bg-sky-100     text-sky-600",
  emerald: "bg-emerald-100 text-emerald-600",
  amber:   "bg-amber-100   text-amber-600",
  violet:  "bg-violet-100  text-violet-600",
  rose:    "bg-rose-100    text-rose-600",
  teal:    "bg-teal-100    text-teal-600",
  orange:  "bg-orange-100  text-orange-600",
  blue:    "bg-blue-100    text-blue-600",
};

const features = [
  { icon: Link2, title: "SnapTrade broker sync", body: "Connect your brokerage read-only and DPP imports positions + cost basis + cash automatically. No copy-paste. Live in Production — Schwab, Fidelity, Vanguard, Robinhood, IBKR, and 40+ others." },
  { icon: Bell, title: "Ex-div push alerts", body: "Install DPP on your phone and get a heads-up the day before every ex-dividend date across every account." },
  { icon: Newspaper, title: "The DPP Weekly", body: "Saturday morning email with your tracked tickers, ex-div dates landing this week, and one Investment 101 lesson." },
  { icon: LineChart, title: "Backtest with real data", body: "Automatic import of historical prices and dividends from Yahoo Finance for any of 8,000+ tickers." },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [waitlist, setWaitlist] = useState(null); // {feature, title, blurb} | null
  const [quizOpen, setQuizOpen] = useState(false);
  const heroImageUrl = useSiteCopy(
    "landing.hero_image_url",
    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=2400&q=80",
  );

  usePageSeo({
    title: "Dividend Portfolio Planner — Plan, Backtest & Track Dividend Income",
    description:
      "Build dividend portfolios, backtest 30+ years of real market data, and track live holdings via SnapTrade. Free forever — no credit card.",
    path: "/",
    ogSlug: "home",
    jsonLdId: "landing-org-jsonld",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Dividend Portfolio Planner",
        alternateName: "DPP",
        url: "https://dividendportfolioplanner.com",
        applicationCategory: "FinanceApplication",
        applicationSubCategory: "Investment Planning",
        operatingSystem: "Web, iOS, Android (PWA)",
        description:
          "Build dividend portfolios, backtest 30+ years of real market data, and track live holdings via SnapTrade. Free forever — no credit card.",
        featureList: [
          "Hypothetical portfolio builder",
          "30+ year historical backtest with DRIP",
          "AI Portfolio Optimizer",
          "AI Income Target Planner (reverse-solve for a monthly income goal)",
          "Live holdings tracking across Roth, Taxable, 401k, IRA, HSA",
          "SnapTrade brokerage sync (read-only)",
          "Ex-dividend push notifications",
          "Weekly personalized dividend digest",
          "Ticker research with AI thesis + safety score",
          "Interactive DRIP calculator and glossary",
        ],
        offers: [
          { "@type": "Offer", price: "0.00", priceCurrency: "USD", name: "Free plan", description: "1 saved portfolio, 4 AI actions per month, 30-year backtests, live holdings for 10 accounts, push alerts, weekly digest" },
          { "@type": "Offer", price: "9.99", priceCurrency: "USD", name: "Core monthly", description: "10 saved portfolios, 10 AI actions per month, SnapTrade broker sync" },
          { "@type": "Offer", price: "14.99", priceCurrency: "USD", name: "Pro monthly", description: "Unlimited saved portfolios, unlimited AI Optimizer, Income Target Planner, and AI Research Chat, priority backtest queue" },
          { "@type": "Offer", price: "143.99", priceCurrency: "USD", name: "Pro yearly", description: "Pro at 20% annual discount" },
          { "@type": "Offer", price: "7.99", priceCurrency: "USD", name: "Team seat", description: "Per-seat billing for teams and financial advisors" },
        ],
        creator: {
          "@type": "Organization",
          name: "Dividend Portfolio Planner",
          url: "https://dividendportfolioplanner.com",
          sameAs: [
            "https://www.youtube.com/@DivPortplan",
          ],
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer support",
            email: "info@dividendportfolioplanner.com",
            url: "https://dividendportfolioplanner.com/contact",
          },
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Dividend Portfolio Planner",
        url: "https://dividendportfolioplanner.com",
        logo: "https://dividendportfolioplanner.com/logo.png",
        sameAs: [
          "https://www.youtube.com/@DivPortplan",
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Dividend Portfolio Planner",
        alternateName: "DPP",
        url: "https://dividendportfolioplanner.com",
        description: "Interactive dividend portfolio planner, backtester, and live-holdings tracker with AI-written explanations.",
        publisher: {
          "@type": "Organization",
          name: "Dividend Portfolio Planner",
          url: "https://dividendportfolioplanner.com",
          logo: "https://dividendportfolioplanner.com/logo.png",
        },
        potentialAction: {
          "@type": "SearchAction",
          target: "https://dividendportfolioplanner.com/research?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "Dividend Portfolio Planner Pro",
        image: "https://dividendportfolioplanner.com/logo.png",
        description:
          "DPP Pro unlocks the AI Portfolio Optimizer, AI Income Target Planner, AI Research Chat, unlimited backtests, and unlimited AI narratives. Free plan available forever.",
        brand: { "@type": "Brand", name: "Dividend Portfolio Planner" },
        category: "Financial Planning Software",
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: "0.00",
          highPrice: "95.88",
          offerCount: 4,
          url: "https://dividendportfolioplanner.com/pricing",
          availability: "https://schema.org/InStock",
          offers: [
            {
              "@type": "Offer",
              name: "Free",
              price: "0.00",
              priceCurrency: "USD",
              url: "https://dividendportfolioplanner.com/pricing",
              availability: "https://schema.org/InStock",
              description: "1 saved portfolio, 4 AI actions per month, 30-year backtests, live holdings for up to 10 accounts, push alerts, weekly newsletter.",
            },
            {
              "@type": "Offer",
              name: "Pro Monthly",
              price: "9.99",
              priceCurrency: "USD",
              url: "https://dividendportfolioplanner.com/pricing",
              availability: "https://schema.org/InStock",
              description: "Unlimited AI Optimizer, Income Target Planner, and AI Research Chat with no monthly cap. Cancel anytime.",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "9.99",
                priceCurrency: "USD",
                billingIncrement: 1,
                unitCode: "MON",
              },
            },
            {
              "@type": "Offer",
              name: "Pro Yearly",
              price: "95.88",
              priceCurrency: "USD",
              url: "https://dividendportfolioplanner.com/pricing",
              availability: "https://schema.org/InStock",
              description: "Pro at 20% annual discount ($7.99/mo billed yearly).",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "95.88",
                priceCurrency: "USD",
                billingIncrement: 1,
                unitCode: "ANN",
              },
            },
            {
              "@type": "Offer",
              name: "Team seat",
              price: "7.99",
              priceCurrency: "USD",
              url: "https://dividendportfolioplanner.com/pricing",
              availability: "https://schema.org/InStock",
              description: "Per-seat billing for teams and financial advisors.",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "7.99",
                priceCurrency: "USD",
                billingIncrement: 1,
                unitCode: "MON",
              },
            },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "How much do I need to invest to earn $1,000 a month in dividends?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Roughly $200,000 to $300,000 at a 4-6% average yield. DPP's Income Target Planner does the exact math against the real yield-on-cost of the tickers you select.",
            },
          },
          {
            "@type": "Question",
            name: "Is DPP a broker? Can I place trades from it?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "No. DPP is planning and tracking software only. It never places trades. Users still execute buys and sells with their own brokerage.",
            },
          },
          {
            "@type": "Question",
            name: "How much does DPP Pro cost?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "DPP Pro is $9.99 per month, or $95.88 per year (20% annual discount). The free tier is available forever with no credit card required.",
            },
          },
          {
            "@type": "Question",
            name: "Where does the historical price and dividend data come from?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Financial Modeling Prep, with Yahoo Finance as a backup for symbols it does not cover. Data refreshes daily after US market close.",
            },
          },
        ],
      },
    ],
  });

  return (
    <div>
      {/* 1. HERO BANNER — full-bleed image with dark navy overlay.
             The image URL is admin-editable via site copy
             (landing.hero_image_url) so the marketing team can swap the
             photo without a deploy. */}
      <section
        className="relative overflow-hidden isolate"
        data-testid="landing-hero"
      >
        <div className="absolute inset-0 -z-10">
          {heroImageUrl && (
            <img
              src={heroImageUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover object-center"
              loading="eager"
            />
          )}
          {/* Uniform navy overlay everywhere so the mountain photo reads on
              small viewports. Horizontal fade layered on top of it only at
              lg+ where the text sits on the left half and we want the right
              side of the photo to show through. */}
          <div className="absolute inset-0 bg-[#0b1a35]/70" />
          <div className="absolute inset-0 hidden lg:block bg-gradient-to-r from-[#0b1a35]/70 via-[#0b1a35]/50 to-[#0b1a35]/20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b1a35]/40 via-transparent to-[#0b1a35]/40" />
        </div>

        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 text-white relative">
          <h1 className="serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl text-white">
            Saving is Not <span className="italic text-secondary">Investing</span>.
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-white max-w-2xl leading-snug font-medium">
            Research, compare and backtest dividend investments in minutes.
          </p>
          <p className="mt-3 text-sm sm:text-base text-white/80 max-w-2xl leading-relaxed">
            See how dividends and price appreciation work together using real historical market data — and let DPP AI explain the results.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button
              data-testid={TEST_IDS.ctaGetStarted}
              className="rounded-lg h-12 px-6 text-base font-semibold bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg shadow-[#2563eb]/25"
              onClick={() => nav(user ? "/portfolio/new" : "/auth?mode=register")}
            >
              {user ? "Open Builder" : "Start Researching Free"}
            </Button>
            <Button
              variant="outline"
              data-testid="landing-quiz-open"
              className="rounded-lg h-12 px-5 text-base font-semibold border-white/60 bg-white/10 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm"
              onClick={() => setQuizOpen(true)}
            >
              <Compass className="w-4 h-4 mr-2" /> Help me choose a plan
            </Button>
            {!user && (
              <div className="mono text-[11px] uppercase tracking-[0.2em] text-white/80">
                Free forever · No credit card required · Cancel anytime
              </div>
            )}
          </div>
        </div>
      </section>

      <PlanPickerQuiz open={quizOpen} onOpenChange={setQuizOpen} onPick={(plan) => nav(`/pricing?plan=${plan}`)} />

      {/* 2. STATS STRIP — 4-col row that sits directly below the hero image */}
      <section
        className="max-w-7xl mx-auto px-6 py-8 border-b border-border/60"
        data-testid="landing-stats-strip"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          <StatIcon icon={Database}   tone="sky"     value="8,000+"           label="Stocks & ETFs" />
          <StatIcon icon={Calendar}   tone="emerald" value="30+ Years"        label="of Historical Data" />
          <StatIcon icon={TrendingUp} tone="amber"   value="Prices + Dividends" label="Real Market Data" />
          <StatIcon icon={Sparkles}   tone="violet"  value="AI Analysis"      label="Plain-English Insights" />
        </div>
      </section>

      {/* 3. ASK DPP — reskinned LandingAIAdvisor. Uses the light-blue card
             look from the reference; the underlying component keeps every
             existing feature (rotating placeholder, LLM funnel, cache,
             analytics tracking, signup-with-seed). */}
      <section
        className="max-w-7xl mx-auto px-6 py-10"
        data-testid="landing-ask-dpp"
      >
        <LandingAIAdvisor />
      </section>

      {/* 4. TOTAL RETURN COMPARISON — live interactive mini-backtester with
             5 tickers + VOO benchmark pre-loaded, real Yahoo data. */}
      <LandingBacktestCompare />

      {/* 5. WHY INVESTORS CHOOSE DPP — 8-column feature row.
             Each tile is a button that launches the /vs Compare card so
             visitors can see exactly how DPP stacks up on any capability
             that resonated with them. */}
      <section
        className="max-w-7xl mx-auto px-6 py-10 border-t border-border/60"
        data-testid="landing-why-dpp"
      >
        <h2 className="serif text-2xl sm:text-3xl mb-6">Why Investors Choose DPP</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
          {WHY_DPP.map((f) => {
            const cls = TONE_STYLES[f.tone] || TONE_STYLES.blue;
            return (
              <button
                key={f.title}
                type="button"
                onClick={() => nav(`/vs/field?from=landing#${f.areaAnchor}`)}
                data-testid={`why-dpp-tile-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex flex-col items-center text-center gap-2 p-3 rounded-xl border border-transparent hover:border-primary/30 hover:bg-primary/[0.03] hover:-translate-y-0.5 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-primary/30"
                title={`See how DPP compares on ${f.title}`}
              >
                <div className={`w-12 h-12 rounded-full grid place-items-center ${cls}`}>
                  <f.icon className="w-6 h-6" strokeWidth={2.5} />
                </div>
                <div className="text-xs font-bold text-foreground leading-tight">{f.title}</div>
                <div className="text-[11px] text-muted-foreground leading-tight font-medium">{f.desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* 6. DIVIDEND CALCULATOR — existing preview widget (kept as-is) */}
      <LandingDividendPreview />

      {/* 7. SEE DPP IN ACTION — 30 SECONDS. Renders the existing product
             screenshot as the thumbnail with a big play button; clicking
             routes to /demo?autoplay=1 which auto-advances the guided tour
             on arrival. */}
      <section
        className="max-w-7xl mx-auto px-6 py-14"
        data-testid="landing-see-in-action"
      >
        <div className="mb-6">
          <h2 className="serif text-2xl sm:text-3xl inline-flex items-center gap-2">
            <PlayCircle className="w-6 h-6 text-primary" /> See DPP in Action — 30 Seconds
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Watch how easy it is to research, compare, backtest and build your dividend portfolio.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-8 items-stretch">
          {/* Thumbnail with play button */}
          <button
            type="button"
            onClick={() => nav("/demo?autoplay=1")}
            data-testid="landing-see-demo-thumb"
            className="relative rounded-2xl overflow-hidden border border-border shadow-xl group focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Play the 30-second DPP demo"
          >
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_dividend-builder-1/artifacts/eyhs3zme_DPP%201.jpg"
              alt="DPP portfolio builder preview — holdings, backtest chart and AI analysis"
              loading="lazy"
              className="w-full h-full object-cover aspect-[16/9] transition-transform duration-700 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            <span className="absolute inset-0 grid place-items-center">
              <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-black/60 backdrop-blur grid place-items-center text-white shadow-2xl transition-transform group-hover:scale-110">
                <PlayCircle className="w-9 h-9 sm:w-11 sm:h-11" />
              </span>
            </span>
          </button>

          {/* Value bullets + CTA */}
          <div className="flex flex-col justify-center">
            <ul className="space-y-2.5">
              {[
                "Research thousands of stocks and ETFs",
                "Backtest with real market data",
                "Model dividends and DRIP",
                "Build and track your portfolio",
                "Get AI explanations for every result",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {line}
                </li>
              ))}
            </ul>
            <Button
              className="mt-5 rounded-lg h-11 px-6 self-start bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
              onClick={() => nav(user ? "/portfolio/new" : "/auth?mode=register")}
              data-testid="landing-see-demo-cta"
            >
              {user ? "Open Builder" : "Create Free Account"}
            </Button>
          </div>
        </div>
      </section>

      {/* 8. BOTTOM CTA BANNER — full-width image band, same photo as hero
             (or admin-swapped) to bookend the experience visually. */}
      <section
        className="relative overflow-hidden mt-6 isolate"
        data-testid="landing-bottom-cta"
      >
        <div className="absolute inset-0 -z-10">
          {heroImageUrl && (
            <img
              src={heroImageUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          )}
          <div className="absolute inset-0 bg-[#0b1a35]/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 py-12 text-white grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] items-center">
          <div>
            <h2 className="serif text-2xl sm:text-3xl leading-tight">Ready to start building your dividend future?</h2>
            <p className="mt-2 text-sm sm:text-base text-white/80">
              Join investors using DPP to research, backtest and track their dividend portfolios.
            </p>
          </div>
          <div className="lg:justify-self-end flex flex-col sm:items-end gap-1.5">
            <Button
              className="rounded-lg h-12 px-6 text-base font-semibold bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg shadow-[#2563eb]/25"
              onClick={() => nav(user ? "/portfolio/new" : "/auth?mode=register")}
              data-testid="landing-bottom-cta-btn"
            >
              {user ? "Open Builder" : "Create Free Account"}
            </Button>
            {!user && (
              <div className="mono text-[11px] uppercase tracking-[0.2em] text-white/70">
                Free forever · No credit card required · Cancel anytime
              </div>
            )}
          </div>
        </div>
      </section>

      <WaitlistDialog
        open={!!waitlist}
        onOpenChange={(o) => !o && setWaitlist(null)}
        feature={waitlist?.feature || ""}
        title={waitlist?.title}
        blurb={waitlist?.blurb}
      />

      {/* Sticky mobile bottom CTA — anonymous visitors only. Fixed to the
          viewport so it follows the reader while they scroll long-form
          content (backtest, calculator, video). Kept out of the way on
          desktop where the hero + bottom banner CTAs are always visible. */}
      {!user && (
        <div
          className="md:hidden fixed inset-x-0 bottom-0 z-40 pointer-events-none"
          data-testid="mobile-sticky-cta-wrap"
          aria-hidden="false"
        >
          <div className="mx-auto max-w-md px-3 pb-3 pt-2 pointer-events-auto">
            <div className="rounded-xl bg-[#0b1a35] shadow-2xl border border-white/10 flex items-center gap-2 p-2">
              <div className="flex-1 min-w-0 px-2 leading-tight">
                <div className="text-white text-sm font-semibold truncate">Ready to start?</div>
                <div className="text-white/60 text-[10px] mono uppercase tracking-widest truncate">Free · no card</div>
              </div>
              <Button
                onClick={() => nav("/auth?mode=register")}
                data-testid="mobile-sticky-cta-btn"
                className="rounded-lg h-10 px-4 text-sm font-semibold bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg shrink-0"
              >
                Create Free Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatIcon({ icon: Icon, value, label, tone = "blue" }) {
  const cls = TONE_STYLES[tone] || TONE_STYLES.blue;
  return (
    <div className="flex items-center gap-3" data-testid={`stat-${value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className={`w-10 h-10 rounded-md grid place-items-center shrink-0 ${cls}`}>
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold text-foreground leading-tight">{value}</div>
        <div className="text-xs text-muted-foreground leading-tight font-medium">{label}</div>
      </div>
    </div>
  );
}

